import { dedent } from 'ts-dedent'
import { DataWarehouseConnection } from '..'
import { Workflow } from '../..'
import { WorkflowComponent } from '../../../components/common/workflowcomponent'
import { Output } from '../../../components/output'
import { ReadTable } from '../../../components/readtable'
import {
  ColumnDataType,
  ExecuteDynamicSQLParams,
  Language,
  Provider,
  TableOptimizationInfo,
  WorkflowAsTasks,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowTask,
  WorkflowVariable
} from '../../../types'
import {
  OUTPUT_TABLE_FQN_VAR_NAME,
  getVariablesUsedInQuery,
  isNumericIntegerValue,
  sortByName
} from '../../../utils'

export class DBricksConnection extends DataWarehouseConnection {
  EXPIRATION_TIME_DAY = 30
  BOUNDS_COLUMNS = [
    {
      name: '__carto_xmin',
      type: ColumnDataType.Number
    },
    {
      name: '__carto_ymin',
      type: ColumnDataType.Number
    },
    {
      name: '__carto_xmax',
      type: ColumnDataType.Number
    },
    {
      name: '__carto_ymax',
      type: ColumnDataType.Number
    }
  ]

  constructor(name: string, tempStoragePath: string) {
    tempStoragePath = tempStoragePath.replace(/`/g, '')
    super(name, tempStoragePath, Provider.DatabricksRest)
    this.supportsCTEs = false
    this.codeLanguage = Language.Python
  }

  datasetName() {
    return this.tempStoragePath.split('.').pop()
  }

  castValue(value: string, type: string) {
    return `CAST(${value} AS ${type})`
  }

  getTempSelectQuery(select: string, dryRun: boolean): string {
    if (dryRun) {
      return dedent`SELECT * FROM
                  (
                    ${select}
                  ) WHERE 1=0`
    } else {
      return select
    }
  }

  getCreateTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    addOptimizationStats = addOptimizationStats ?? true
    select = dedent(select)
    tablename = this.applyCapitalization(this.quoteName(tablename))
    const dryRun = codeType === WorkflowCodeType.DryRun
    const cluster =
      !dryRun && optimizeBy?.column
        ? '\n' +
          dedent`
            CLUSTER BY (${optimizeBy.column.name})`
        : ''
    const cache =
      codeType === WorkflowCodeType.FullNoCache ? '' : ' IF NOT EXISTS'
    return dedent`CREATE TABLE${cache} ${tablename}${cluster}
              AS
                ${this.getTempSelectQuery(select, dryRun)};
              ${
                !dryRun && addOptimizationStats
                  ? `ANALYZE TABLE ${tablename} COMPUTE STATISTICS;`
                  : ''
              }`
  }

  getCreateTempTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    addOptimizationStats = addOptimizationStats ?? true
    select = dedent(select)
    tablename = this.applyCapitalization(this.quoteName(tablename))
    const dryRun = codeType === WorkflowCodeType.DryRun
    let create = ''
    if (codeType === WorkflowCodeType.FullNoCache) {
      // we cannot use CREATE OR REPLACE here, since it might fail
      // if the clustering column has changed
      create = dedent`DROP TABLE IF EXISTS ${tablename};
                        CREATE TABLE`
    } else {
      create = 'CREATE TABLE IF NOT EXISTS'
    }
    const cluster =
      !dryRun && optimizeBy?.column
        ? `\nCLUSTER BY (${optimizeBy.column.name})`
        : ''
    return dedent`${create} ${tablename}${cluster}
                    AS
                      ${this.getTempSelectQuery(
                        select,
                        codeType === WorkflowCodeType.DryRun
                      )};
                    ${
                      addOptimizationStats &&
                      [
                        WorkflowCodeType.Full,
                        WorkflowCodeType.FullNoCache
                      ].includes(codeType)
                        ? this.optimizationStatsQuery(tablename)
                        : ''
                    }`
  }

  optimizationStatsQuery(tablename: string): string {
    tablename = this.applyCapitalization(this.quoteName(tablename))
    return dedent`ANALYZE TABLE ${tablename} COMPUTE STATISTICS;`
  }

  // To be used for temp tables when running in a stored procedure,
  // since we cannot create session temp tables
  _getTempSchemaPath(workflowId: string) {
    return `${this.tempStoragePath.split('.')[0]}.temp_${workflowId}`
  }

  getOutputTableName(
    workflowId: string,
    componentName: string,
    params: Record<string, unknown>,
    output: string,
    codeType: WorkflowCodeType
  ): string {
    const name = super.getOutputTableName(
      workflowId,
      componentName,
      params,
      output,
      codeType
    )
    if (codeType === WorkflowCodeType.StoredProcedure) {
      return this.applyCapitalization(
        `${this._getTempSchemaPath(workflowId)}.${name}`
      )
    } else {
      return this.applyCapitalization(name)
    }
  }

  wrapSQLCodeAsPythonCall(sql: string, f?: boolean) {
    // in case python variables want to be passed inside the sql code
    // please use the following syntax: ''' + my_var + '''
    // this will concatenate the variable value inside the sql code
    return sql
      .replace(/BEGIN\n([\s\S]*?)END;/, '$1')
      .split(';')
      .filter((s) => /\S/.test(s))
      .map(
        (s) => dedent`spark.sql(${f ? 'f' : ''}'''
          ${dedent(s)}
        ''')`
      )
      .join('\n')
  }

  generateSpatialFilterTables(
    mainTable: string,
    secondaryTable: string,
    mainColumn: string,
    secondaryColumn: string,
    leftOutputTableName: string,
    rightOutputTableName: string
  ) {
    // This code is used to generate the spatial filter tables containing the bounding boxes of the geometries
    // they are used to optimize the spatial join between the two tables
    return `
spark.sql(f'''DROP TABLE IF EXISTS ${leftOutputTableName}''')
spark.sql(f'''
          CREATE TABLE IF NOT EXISTS ${leftOutputTableName}
          AS
          ( SELECT /*+ REPARTITION(40, ${mainColumn}) */
              *,
              ST_XMIN(ST_GEOMFROMWKB(${mainColumn})) AS __carto_bounds_xmin,
              ST_YMIN(ST_GEOMFROMWKB(${mainColumn})) AS __carto_bounds_ymin,
              ST_XMAX(ST_GEOMFROMWKB(${mainColumn})) AS __carto_bounds_xmax,
              ST_YMAX(ST_GEOMFROMWKB(${mainColumn})) AS __carto_bounds_ymax
            FROM ${mainTable}
          )
          CLUSTER BY (__carto_bounds_xmin, __carto_bounds_ymin, __carto_bounds_xmax, __carto_bounds_ymax)
          ''')

spark.sql(f'''DROP TABLE IF EXISTS ${rightOutputTableName}''')
spark.sql(f'''
          CREATE TABLE IF NOT EXISTS ${rightOutputTableName}
          AS
          ( SELECT /*+ REPARTITION(40, ${secondaryColumn}) */
              *,
              ST_XMIN(ST_GEOMFROMWKB(${secondaryColumn})) AS __carto_bounds_xmin,
              ST_YMIN(ST_GEOMFROMWKB(${secondaryColumn})) AS __carto_bounds_ymin,
              ST_XMAX(ST_GEOMFROMWKB(${secondaryColumn})) AS __carto_bounds_xmax,
              ST_YMAX(ST_GEOMFROMWKB(${secondaryColumn})) AS __carto_bounds_ymax
            FROM ${secondaryTable}
          )
          CLUSTER BY (__carto_bounds_xmin, __carto_bounds_ymin, __carto_bounds_xmax, __carto_bounds_ymax)
          ''')

bin_size = spark.sql(f'''
          SELECT ROUND(max(v), 2) AS v
          FROM (
            SELECT EXPLODE(APPROX_PERCENTILE(CAST(__carto_bounds_xmax - __carto_bounds_xmin AS DOUBLE), ARRAY(0.95), 1000000)) AS v
            FROM ${leftOutputTableName}
            UNION
            SELECT EXPLODE(APPROX_PERCENTILE(CAST(__carto_bounds_ymax - __carto_bounds_ymin AS DOUBLE), ARRAY(0.95), 1000000)) AS v
            FROM ${leftOutputTableName}
            UNION
            SELECT EXPLODE(APPROX_PERCENTILE(CAST(__carto_bounds_xmax - __carto_bounds_xmin AS DOUBLE), ARRAY(0.95), 1000000)) AS v
            FROM ${rightOutputTableName}
            UNION
            SELECT EXPLODE(APPROX_PERCENTILE(CAST(__carto_bounds_ymax - __carto_bounds_ymin AS DOUBLE), ARRAY(0.95), 1000000)) AS v
            FROM ${rightOutputTableName}
          )''').collect()[0]['v']

spark.conf.set("spark.databricks.optimizer.rangeJoin.binSize", f"{bin_size}")`
  }

  executeDynamicSQL({ sql, addQuotes, replacements }: ExecuteDynamicSQLParams) {
    const lines = sql
      .replace(/BEGIN\n([\s\S]*?)END;/, '$1')
      .split(';')
      .filter((s) => /\S/.test(s))
    if (replacements) {
      return lines
        .map(
          (q) => dedent`spark.sql(
                          ${replacements?.reduce(
                            (acc, [org, repl]) =>
                              dedent`${acc}.replace(
                                ${org},
                                ${repl}
                              )
                              `,
                            addQuotes
                              ? this.quoteMultilineString(dedent(q))
                              : dedent(q)
                          )}
                        )`
        )
        .join('\n')
    } else {
      return lines
        .map(
          (q) => dedent`spark.sql(
                          ${
                            addQuotes
                              ? this.quoteMultilineString(dedent(q))
                              : dedent(q)
                          }
                        )`
        )
        .join('\n')
    }
  }

  wrapComponentCodeInFlowControlBlock(
    query: string,
    codeType: WorkflowCodeType,
    language: Language
  ): string {
    if (language === Language.SQL) {
      query = this.wrapSQLCodeAsPythonCall(query)
    }
    if (codeType === WorkflowCodeType.DryRun) {
      return dedent`
                try:
                    ${query}
                except Exception as e:
                    pass`
    } else {
      return query
    }
  }

  wrapCodeInIfTableExistsBlock(
    query: string,
    outputTables: Array<string>,
    codeType: WorkflowCodeType,
    outputTablesExpressions?: Array<string>,
    variablePrefix?: string
  ): string {
    variablePrefix = variablePrefix || ''
    if (
      [WorkflowCodeType.DryRun, WorkflowCodeType.StoredProcedure].includes(
        codeType
      )
    ) {
      return query
    } else if (codeType === WorkflowCodeType.FullNoCache) {
      return dedent`
        ${outputTables
          .map((x) => `spark.sql('DROP TABLE IF EXISTS ${this.quoteName(x)}')`)
          .join('\n')}
        ${query}
      `
    } else {
      const outputTablesLiteralNames = JSON.stringify(outputTables)
      const outputTablesExpressionNames = `[${
        outputTablesExpressions?.join(', ') || ''
      }]`
      return dedent`
      def table_exists(table:str):
        catalog, schema, table_name = table.replace('\`', '').split('.')
        query = spark.sql(f"""
                SELECT 1
                FROM \`{catalog}\`.information_schema.tables
                WHERE table_name = LOWER('{table_name}')
                AND table_schema = LOWER('{schema}') LIMIT 1""",
            )
        return query.count() > 0
      tableNames = ${outputTablesLiteralNames}
      tableNames += ${outputTablesExpressionNames}
      tableMissing = any([(not table_exists(table)) for table in tableNames])
      if tableMissing:
        ${query}
      `
    }
  }

  wrapCodeInPrepareForVisualizationBlock(
    inputTable: string,
    outputTable: string,
    geoColumn: string,
    codeType: WorkflowCodeType
  ): string {
    const select = dedent`SELECT /*+ REPARTITION(40) */
    ST_XMIN(${geoColumn}_processed) AS __carto_xmin,
    ST_YMIN(${geoColumn}_processed) AS __carto_ymin,
    ST_XMAX(${geoColumn}_processed) AS __carto_xmax,
    ST_YMAX(${geoColumn}_processed) AS __carto_ymax,
    * EXCEPT(${geoColumn}_processed)
  FROM (
    SELECT ${this.processInputGeogColumn(
      geoColumn as string
    )} AS ${geoColumn}_processed,
      ''' + select_clause_str + '''
    FROM ${this.quoteName(inputTable as string)}
  )`

    const createTable = this.getCreateTempTableQuery(
      outputTable,
      select,
      codeType,
      null
    )

    return `
from pyspark.sql.functions import col

df = spark.sql("select * from ${this.quoteName(inputTable as string)}")


# Specify the special columns
special_columns = ['__carto_xmin', '__carto_ymin', '__carto_xmax', '__carto_ymax']

# Get the special columns that are in the table
excluded_columns = [col_name for col_name in special_columns if col_name in df.columns]

rename_geom_column = ''
geom_alias = 'l_geom'
if '${geoColumn}' != 'geom':
    if 'geom' in df.columns:
        # Iterate to find a valid alias
        while geom_alias in df.columns:
            geom_alias = 'l_' + geom_alias
        rename_geom_column = rename_geom_column + f', geom AS {geom_alias}'
        excluded_columns.append('geom')
    rename_geom_column = rename_geom_column + f', ${geoColumn} AS geom'


excluded_columns_string = ''
if len(excluded_columns) > 0:
    excluded_columns_string = ', '.join(excluded_columns)
    excluded_columns_string = f' EXCEPT({excluded_columns_string})'

select_clause_str = f'*{excluded_columns_string}{rename_geom_column}'

${this.wrapSQLCodeAsPythonCall(createTable)}
  `
  }

  async getWorkflowCode(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas,
    preQuery?: string
  ): Promise<string> {
    const queries = await workflow.getSubqueries(
      this,
      envVars,
      codeType,
      schemas
    )
    const executable = queries.some((q) => q.query)
    let query = ''
    const previewPreparationCode =
      'spark.conf.set("spark.databricks.delta.optimize.skipping", "true")'
    if (executable) {
      if (codeType !== WorkflowCodeType.StoredProcedure) {
        let query = await this._getScriptBody(
          workflow,
          envVars,
          queries,
          codeType,
          schemas
        )
        query = dedent`
                '''
                ${workflow.workflowMetadataAsCommentString(codeType)}
                '''
                ${previewPreparationCode}
                ${preQuery || ''}${query}`
        query = this.replaceVariablesInScriptQuery(query, workflow.variables)
        return query
      } else {
        throw new Error('Stored procedures are not supported in Databricks')
      }
    }

    return query
  }

  paramTypeToDataProviderType(
    inputType: WorkflowComponentParamType,
    inputValue: string
  ) {
    switch (inputType) {
      case WorkflowComponentParamType.String:
        return 'STRING'
      case WorkflowComponentParamType.Number:
        return isNumericIntegerValue(inputValue) ? 'BIGINT' : 'DOUBLE'
      case WorkflowComponentParamType.Boolean:
        return 'BOOLEAN'
      default:
        return 'STRING'
    }
  }

  columnTypeToDataProviderType(colType: ColumnDataType) {
    switch (colType) {
      case ColumnDataType.String:
        return 'STRING'
      case ColumnDataType.Number:
        return 'DOUBLE'
      case ColumnDataType.Boolean:
        return 'BOOLEAN'
      case ColumnDataType.Binary:
        return 'BINARY'
      default:
        return 'STRING'
    }
  }

  extractErrorLineNumberFromApiResponse(response: string): number | null {
    const match = response.match(/line (\d+)$/)
    if (match) {
      return parseInt(match[1])
    } else {
      return null
    }
  }

  /**
   *
   * @param workflow
   * @returns The list of non-accessible tables. However, databricks writes the error traces
   * to the standard output, so we cannot get only the list of tables. To fix that, the array
   * of tables names is returned enclosed as follows, so the caller can parse it.
   * ###noaccess###[table1, table2, ...]###noaccess###
   */
  getSourceTablePermissionsQuery(workflow: Workflow) {
    const tables: Array<string> = []
    for (const [componentName, component] of Object.entries(
      workflow.components
    )) {
      if (component.metadata.name === new ReadTable().metadata.name) {
        const value = workflow.componentValues[componentName]['source']
        const tablename = (value as WorkflowComponentValue).value as string
        if (!tables.includes(tablename)) {
          tables.push(tablename)
        }
      }
    }
    if (tables.length === 0) {
      return ''
    }
    const query = dedent`
      tables =[${tables.map((x) => `'${x}'`).join(', ')}]
      noAccess = []
      for table in tables:
        try:
          spark.sql(f'SELECT * FROM {table} WHERE 1=0')
        except Exception as e:
          noAccess.append(table)
      f'###noaccess###{noAccess}###noaccess###'`

    return query
  }

  /**
   * Returns a query that can be used in the datawarehouse to setup the temporary storage required to run a workflow.
   * If the query, once executed on the DW, returns no errors, it means that the connection is ready to run a workflow
   * on it. Otherwise, it will return a description of the problems encountered, ready to be shown to the user with
   * indications on how to solve them. Databricks writes the error traces to the standard output, so we cannot
   * get only the list of tables. To fix that, the array of tables names is returned enclosed as follows, so the caller
   * can parse it:
   * ###noaccess###[table1, table2, ...]###noaccess###
   */
  getTempStorageSetupQueries(options?: any) {
    return [
      dedent`
        errorcode = None
        if spark.catalog.databaseExists('${this.quoteName(
          this.tempStoragePath
        )}'):
          try:
            spark.sql('DROP TABLE IF EXISTS ${this.quoteName(
              this.tempStoragePath
            )}.__testtable')
            spark.sql('CREATE TABLE ${this.quoteName(
              this.tempStoragePath
            )}.__testtable AS (SELECT 1 AS num)')
            spark.sql('DROP TABLE IF EXISTS ${this.quoteName(
              this.tempStoragePath
            )}.__testtable')
          except Exception as e:
            errorcode = 'temp_storage_create_tables'
        else:
          try:
            spark.sql('CREATE DATABASE ${this.quoteName(this.tempStoragePath)}')
          except Exception as e:
            errorcode = 'temp_storage_access_create'
        if errorcode is None:
          message = '{"success": true}'
        else:
          message = '{"success": false, "error": "' + errorcode + '"}'
        f'###tempstorage###{message}###tempstorage###'
        `
    ]
  }

  getTableListQuery(filter: string): string {
    // This function returns a SQL query
    const [database, schema] = this.tempStoragePath?.split('.')
    filter = this.applyCapitalization(filter)
    return dedent`
      SELECT table_name
      FROM \`${database}\`.INFORMATION_SCHEMA.TABLES
      WHERE table_schema = '${schema}' AND table_name LIKE '${filter}'`
  }

  dataProviderTypeToColumnType(type: string): ColumnDataType {
    switch (type) {
      case 'BIGINT':
      case 'SMALLINT':
      case 'TINYINT':
      case 'DOUBLE':
      case 'FLOAT':
      case 'LONG':
      case 'INT':
        return ColumnDataType.Number
      case 'BOOLEAN':
        return ColumnDataType.Boolean
      case 'DATE':
        return ColumnDataType.Date
      case 'TIMESTAMP':
        return ColumnDataType.Timestamp
      case 'BINARY':
        return ColumnDataType.Binary
      default:
        return ColumnDataType.String
    }
  }

  quoteName(name: string) {
    name = name.replace(/`/g, '')
    const tokens = name.split('.')
    if (tokens.length === 1) {
      return name
    } else {
      return tokens.map((x) => `\`${x}\``).join('.')
    }
  }

  quoteMultilineString(str: string) {
    return dedent`f'''${str}'''`
  }

  quoteVariableName(name: string) {
    return `\`${name}\``
  }

  processInputGeogColumn(name: string) {
    return `ST_GEOMFROMWKB(${name})`
  }

  processOutputGeogColumn(name: string) {
    return `ST_ASWKB(${name})`
  }

  adaptComponent(component: WorkflowComponent) {
    for (const input of component.inputs) {
      if (input.type === WorkflowComponentParamType.Column && input.dataType) {
        input.dataType = (input.dataType as Array<ColumnDataType>).filter(
          (x) => x !== ColumnDataType.Geography
        )
      }
    }
    component.inputs = component.inputs.filter(
      (x) =>
        x.providers == null || x.providers.includes(Provider.DatabricksRest)
    )
  }

  applyCapitalization(tablename: string): string {
    return tablename.toLowerCase()
  }

  //////////////////////////////////////////

  getStoredProcedureOutputTableNameExpression(
    workflow: Workflow,
    full: boolean
  ): string {
    let root = workflow.getStoredProcedureOutputTableFullName(this)
    return `'${root}_' + hashlib.md5(b'')[:16]`

    // At the moment, the option of using public vars is disabled
    const publicVars = workflow.variables
      .filter((v) => v.public)
      .sort(sortByName)
      .map((v) => v.name)
    if (!full) {
      root = root.split('.').pop() as string
    }
    if (publicVars.length === 0) {
      return `'${root}_' + hashlib.md5(b'')[:16]`
    } else {
      return `'${root}_' + hashlib.md5(json.dumps([${publicVars.join(
        ', '
      )}]).encode('utf-8'))[:16]`
    }
  }

  replaceVariablesInTaskQuery(
    variables: Array<WorkflowVariable>,
    query: string
  ): string {
    const varnames = getVariablesUsedInQuery(query, variables)
    if (varnames.length === 0) {
      return query
    }
    for (const varname of varnames) {
      query = query.replace(RegExp(`@${varname}`, 'g'), `{_jobvar_${varname}}`)
    }
    const declarations = varnames
      .map((varname) => {
        return dedent`
          dbutils.widgets.text('_jobvar_${varname}', '')
          _jobvar_${varname} = dbutils.widgets.get('_jobvar_${varname}')`
      })
      .join('\n')
    return dedent`
              ${declarations}
              ${query}`
  }

  async getAsTasksList(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    isImmutable,
    schemas?: WorkflowTableSchemas | undefined
  ): Promise<WorkflowAsTasks> {
    const CREATE_TEMP_SCHEMA_TASK_NAME = 'createtempschema'
    const DROP_TEMP_SCHEMA_TASK_NAME = 'droptempschema'
    const tasks: Array<WorkflowTask> = []
    const createSchemaQuerySql = dedent`
      DROP SCHEMA IF EXISTS ${this.quoteName(
        this._getTempSchemaPath(workflow.id)
      )} CASCADE;
      CREATE SCHEMA IF NOT EXISTS ${this.quoteName(
        this._getTempSchemaPath(workflow.id)
      )}`
    const createSchemaQuery = this.executeDynamicSQL({
      sql: createSchemaQuerySql,
      addQuotes: true
    })
    tasks.push({
      run_if: 'ALL_SUCCESS',
      task_key: CREATE_TEMP_SCHEMA_TASK_NAME,
      notebook_task: {
        notebook_path: `CARTO/Workflows/${workflow.id}/${CREATE_TEMP_SCHEMA_TASK_NAME}`,
        content: Buffer.from(createSchemaQuery).toString('base64'),
        language: 'PYTHON'
      },
      depends_on: []
    })
    const queries = await workflow.getSubqueries(
      this,
      envVars,
      WorkflowCodeType.StoredProcedure,
      schemas
    )

    const executableComponents: Array<string> = []
    const executable = queries.some((q) => q.query)
    if (executable) {
      for (const query of queries) {
        const component = workflow.components[query.componentName]
        let fullQuery = ''
        if (component.metadata.name === new Output().metadata.name) {
          const workflowOutputSourceTableName =
            workflow.getStoredProcedureOutputSourceTableName(this)
          const outputTableFqnVariable = `${OUTPUT_TABLE_FQN_VAR_NAME} = ${this.getStoredProcedureOutputTableNameExpression(
            workflow,
            true
          )};`
          fullQuery = this.getCreateTempTableQuery(
            '##TABLENAME##',
            'SELECT * FROM ' +
              this.quoteName(workflowOutputSourceTableName as string),
            WorkflowCodeType.StoredProcedure,
            null
          )
          fullQuery = this.executeDynamicSQL({
            sql: fullQuery,
            addQuotes: true,
            replacements: [['`##TABLENAME##`', OUTPUT_TABLE_FQN_VAR_NAME]]
          })
          fullQuery = dedent`
                        import hashlib
                        import json
                        ${outputTableFqnVariable}
                        ${OUTPUT_TABLE_FQN_VAR_NAME} = [f"\`{x}\`" for x in ${OUTPUT_TABLE_FQN_VAR_NAME}.split('.')].join('.')
                        ${fullQuery}`
        } else {
          if (query.query) {
            // we treat all variables as non-public, to avoid limitations in databricks
            fullQuery = this.replaceVariablesInScriptQuery(
              query.query,
              workflow.variables
            )
          }
        }
        if (fullQuery) {
          let dependsOn = workflow
            .componentsInmediatelyUpstream(query.componentName)
            .filter((c) => executableComponents.includes(c))
          if (dependsOn.length === 0) {
            dependsOn = [CREATE_TEMP_SCHEMA_TASK_NAME]
          }
          executableComponents.push(query.componentName)
          tasks.push({
            run_if: 'ALL_SUCCESS',
            task_key: query.componentName,
            notebook_task: {
              notebook_path: `CARTO/Workflows/${workflow.id}/${query.componentName}`,
              content: Buffer.from(fullQuery).toString('base64'),
              language: 'PYTHON'
            },
            depends_on: dependsOn.map((c) => {
              return {
                task_key: c
              }
            })
          })
        }
      }
    }

    const dropSchemaQuerySql = dedent`
      DROP SCHEMA IF EXISTS ${this.quoteName(
        this._getTempSchemaPath(workflow.id)
      )} CASCADE`
    const dropSchemaQuery = this.executeDynamicSQL({
      sql: dropSchemaQuerySql,
      addQuotes: true
    })
    tasks.push({
      run_if: 'ALL_SUCCESS',
      task_key: DROP_TEMP_SCHEMA_TASK_NAME,
      notebook_task: {
        notebook_path: `CARTO/Workflows/${workflow.id}/${DROP_TEMP_SCHEMA_TASK_NAME}`,
        content: Buffer.from(dropSchemaQuery).toString('base64'),
        language: 'PYTHON'
      },
      depends_on: [{ task_key: executableComponents.pop() as string }]
    })

    const description = {
      versionId: workflow.workflowVersionHash(),
      isImmutable: isImmutable
    }
    return {
      name: workflow.id,
      description: JSON.stringify(description),
      tasks
    }
  }

  getStoredProcedureCacheInvalidationQueries(
    workflow: Workflow
  ): Array<string> {
    const [database, schema] = this.tempStoragePath?.split('.')
    const tablePrefix = workflow.getStoredProcedureOutputTableName(this)
    return [
      dedent`
      schema_name = "${schema}"
      table_prefix ="${tablePrefix}"

      tables_df = spark.sql(f"SHOW TABLES IN {schema_name}")
      tables_to_drop = tables_df.filter(tables_df.tableName.startswith(table_prefix)).select("tableName").collect()

      for row in tables_to_drop:
          table_name = row["tableName"]
          spark.sql(f"DROP TABLE IF EXISTS {schema_name}.{table_name}")
    `
    ]
  }

  getOutputSchemasQueries(tableNames: Array<string>): string {
    const [database, schema] = this.tempStoragePath.split('.')
    return dedent`
      result = ${this.wrapSQLCodeAsPythonCall(dedent`
        WITH cols AS (
          SELECT
            table_name,
            column_name AS name,
            data_type AS db_type,
            CASE data_type
              WHEN 'NUMERIC' THEN '${ColumnDataType.Number}'
              WHEN 'SMALLINT' THEN '${ColumnDataType.Number}'
              WHEN 'TINYINT' THEN '${ColumnDataType.Number}'
              WHEN 'INT' THEN '${ColumnDataType.Number}'
              WHEN 'DOUBLE' THEN '${ColumnDataType.Number}'
              WHEN 'FLOAT' THEN '${ColumnDataType.Number}'
              WHEN 'LONG' THEN '${ColumnDataType.Number}'
              WHEN 'BOOLEAN' THEN '${ColumnDataType.Boolean}'
              WHEN 'DATE' THEN '${ColumnDataType.Date}'
              WHEN 'TIMESTAMP' THEN '${ColumnDataType.Timestamp}'
              WHEN 'BINARY' THEN '${ColumnDataType.Binary}'
              ELSE '${ColumnDataType.String}'
            END AS type
          FROM \`${database}\`.INFORMATION_SCHEMA.COLUMNS
          WHERE
            table_name IN (${tableNames
              .map((t) => `'${this.applyCapitalization(t)}'`)
              .join(', ')})
            AND table_schema = '${this.applyCapitalization(schema)}'
        )
        SELECT
          CONCAT('${this.tempStoragePath}.', ANY_VALUE(table_name)) AS fqn,
          TO_JSON(
            ARRAY_AGG(
              NAMED_STRUCT(
                'name', name,
                'type', type,
                'db_type', db_type
              )
            )
          ) AS schema
        FROM cols
        GROUP BY table_name;`)}
      display(result)
    `
  }

  prepareSchemaQueries(queries: Array<string>): Array<string> {
    return [queries.join('\n')]
  }

  getGeoDataType(): ColumnDataType {
    return ColumnDataType.Binary
  }

  wrapSQLCodeAs(query: string) {
    return this.wrapSQLCodeAsPythonCall(query)
  }

  ////////////////////////////////////////////////////////////////////
  // These functions do not apply for this provider, since we have
  // specific endpoints for this, instead of using SQL

  getCheckStoredProcedureExistsQueries(
    workflow: Workflow,
    isImmutable: boolean
  ) {
    return ['']
  }

  getRemoveStoredProcedureQuery(workflow: Workflow): string {
    return ''
  }

  replaceVariablesInStoredProcedureQuery(
    variables: Array<WorkflowVariable>,
    query: string,
    requiresDynamicSQL: boolean
  ) {
    return query
  }

  wrapCodeInProcedure(
    name: string,
    workflow: Workflow,
    body: string,
    includeDiagram: boolean,
    isImmutable: boolean
  ): string {
    return body
  }

  //////////////////////////////////////////////////////////////////////
}
